
import { getApiErrorMessage } from "@core/utility/get-error-message";
import {
  createVehicleController,
  CreateVehicleModel,
  CreateVehicleModelExtension,
  deleteVehicleController,
  DeleteVehicleModel,
  GetResponseVehicleModel,
  getVehicleByCardSeriesController,
  getVehicleController,
  MutateResponseVehicleModel,
  QueryKeyVehicle,
  updateVehicleController,
  UpdateVehicleModel,
  UpdateVehicleModelExtension,
} from "@factory/model/query-model-vehicle";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { AxiosError } from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { authHeaders } from "../authentication/auth-header";
import { useSessionContext } from "../authentication/session.context";

export function useQyGetVehicle(
  search: string,
  limit = 99,
  offset = 0,
  order?: object,
  filter?: Record<string, string>,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseVehicleModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    search: string | undefined = undefined,
    limit: number | undefined = undefined,
    offset: number | undefined = undefined,
    order: object | undefined = undefined,
    filter: Record<string, string> | undefined = undefined,
    dateFilter: string | undefined = undefined,
    startDate: string | undefined = undefined,
    endDate: string | undefined = undefined
  ) => {
    await checkSession();

    showProgress();
    const operation = await getVehicleController(
      search,
      limit,
      offset,
      order,
      { filter: JSON.stringify(filter) },
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseVehicleModel;
  };

  return useQuery({
    enabled,
    queryKey: [
      QueryKeyVehicle,
      search,
      limit,
      offset,
      order,
      filter,
    ],
    queryFn: () =>
      apiFn(
        search,
        limit,
        offset,
        order,
        filter,
      ),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetVehicleById(
  id: string,
  onSuccess?:
    | ((data: GetResponseVehicleModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (id: string, search = "", limit = 1, offset = 0) => {
    await checkSession();

    showProgress();
    const operation = await getVehicleController(
      search,
      limit,
      offset,
      undefined,
      { filter: JSON.stringify({ code: id }) },
      undefined,
      undefined,
      undefined,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseVehicleModel;
  };

  return useQuery({
    queryKey: [QueryKeyVehicle, id],
    queryFn: () => apiFn(id),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetVehicleByCardSeries(
  cardSeries: string,
  enabled = true,
  onSuccess?:
    | ((data: GetResponseVehicleModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();
  const apiFn = async (cardSeries: string) => {
    await checkSession();
    showProgress();
    const operation = await getVehicleByCardSeriesController(
      cardSeries,
      true, undefined, undefined, undefined, undefined, undefined, undefined,
      authHeaders()
    );
    const response = (await operation()).data;
    return response as any;
  };
  return useQuery({
    queryKey: [QueryKeyVehicle, cardSeries],
    queryFn: () => apiFn(cardSeries),
    enabled: enabled && cardSeries !== "",
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);
      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyCreateVehicle(
  onSuccess?:
    | ((data: MutateResponseVehicleModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: CreateVehicleModelExtension) => {
    await checkSession();

    showProgress();
    const operation = await createVehicleController(
      payload.vehicle,
      payload.file,
      authHeaders()
    );
    const response = (await operation()).data;
    return response as MutateResponseVehicleModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyVehicle);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyUpdateVehicle(
  onSuccess?:
    | ((data: MutateResponseVehicleModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: UpdateVehicleModelExtension) => {
    await checkSession();

    showProgress();
    const operation = await updateVehicleController(
      payload.vehicle,
      payload.file,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["message"] as MutateResponseVehicleModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyVehicle);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyDeleteVehicle(
  onSuccess?:
    | ((data: MutateResponseVehicleModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: DeleteVehicleModel) => {
    await checkSession();

    showProgress();
    const operation = await deleteVehicleController(payload, authHeaders());
    const response = (await operation()).data;
    return response["message"] as MutateResponseVehicleModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyVehicle);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}
